import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { getSettings } from "../services/settings";

export type KeyboardPosition =
  | "Bottom Center"
  | "Top Center"
  | "Top Left"
  | "Top Right";

const AUTH_PATHS = [
  "/login",
  "/signup",
  "/forgot-password",
  "/reset-password",
];

export function isAuthKeyboardPage(pathname = window.location.pathname): boolean {
  return (
    AUTH_PATHS.some((p) => pathname.startsWith(p)) ||
    pathname.includes("/password/reset/")
  );
}

function isValidKeyboardInput(
  target: HTMLElement
): target is HTMLInputElement | HTMLTextAreaElement {
  if (target.tagName === "TEXTAREA") return true;
  if (target.tagName !== "INPUT") return false;
  const type = (target as HTMLInputElement).type;
  return ["text", "password", "tel", "email", "url", "number", "search"].includes(
    type
  );
}

interface KeyboardContextType {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  activeInput: HTMLInputElement | HTMLTextAreaElement | null;
  setActiveInput: (
    input: HTMLInputElement | HTMLTextAreaElement | null
  ) => void;
  isEnabled: boolean;
  position: KeyboardPosition;
  refreshConfig: () => Promise<void>;
  /** Auth (login etc.) mobile/tablet: show floating keyboard toggle */
  showAuthToggle: boolean;
  authToggleOn: boolean;
  toggleAuthKeyboard: () => void;
}

const KeyboardContext = createContext<KeyboardContextType | undefined>(
  undefined
);

export const KeyboardProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeInput, setActiveInput] = useState<
    HTMLInputElement | HTMLTextAreaElement | null
  >(null);
  const [backendEnabled, setBackendEnabled] = useState(false);
  const [position, setPosition] = useState<KeyboardPosition>("Bottom Center");
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 1024
  );
  const [isAuthPage, setIsAuthPage] = useState(
    () => typeof window !== "undefined" && isAuthKeyboardPage()
  );
  const [authToggleOn, setAuthToggleOn] = useState(false);

  const showAuthToggle = isAuthPage && isMobileOrTablet;

  // App pages: backend VKB setting only.
  // Auth pages: FAB toggle on mobile/tablet only (no company settings yet on login).
  const isEnabled = useMemo(() => {
    if (isAuthPage) {
      return isMobileOrTablet && authToggleOn;
    }
    return backendEnabled;
  }, [isAuthPage, isMobileOrTablet, authToggleOn, backendEnabled]);

  const syncRouteAndSize = useCallback(() => {
    const mobile = window.innerWidth < 1024;
    const auth = isAuthKeyboardPage();
    setIsMobileOrTablet(mobile);
    setIsAuthPage(auth);
    if (!auth) {
      setAuthToggleOn(false);
    }
  }, []);

  const refreshConfig = useCallback(async () => {
    const token = localStorage.getItem("token");
    let enabled = false;

    if (token) {
      try {
        const response = await getSettings();
        if (response.success && response.data.settings) {
          enabled = !!response.data.settings.vkb_is_visible;
          setPosition(
            (response.data.settings.vkb_position as KeyboardPosition) ||
              "Bottom Center"
          );
        }
      } catch (error) {
        console.error("Failed to fetch keyboard settings:", error);
      }
    }

    setBackendEnabled(enabled);
    (window as any)._keyboardBackendEnabled = enabled;
    syncRouteAndSize();
  }, [syncRouteAndSize]);

  const toggleAuthKeyboard = useCallback(() => {
    setAuthToggleOn((prev) => {
      const next = !prev;
      if (!next) {
        setIsOpen(false);
        setActiveInput(null);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    refreshConfig();

    const handleRecheck = () => {
      const enabled = !!(window as any)._keyboardBackendEnabled;
      setBackendEnabled(enabled);
      syncRouteAndSize();
    };

    window.addEventListener("resize", handleRecheck);
    document.addEventListener("focusin", handleRecheck);

    return () => {
      window.removeEventListener("resize", handleRecheck);
      document.removeEventListener("focusin", handleRecheck);
    };
  }, [refreshConfig, syncRouteAndSize]);

  // Close keyboard when feature is disabled
  useEffect(() => {
    if (!isEnabled) {
      setIsOpen(false);
    }
  }, [isEnabled]);

  // If user turns auth FAB on while an input is already focused, open keyboard
  useEffect(() => {
    if (!isEnabled || !isAuthPage) return;
    const el = document.activeElement as HTMLElement | null;
    if (el && isValidKeyboardInput(el)) {
      setActiveInput(el);
      setIsOpen(true);
    }
  }, [isEnabled, isAuthPage, authToggleOn]);

  useEffect(() => {
    const handleFocus = (e: FocusEvent | MouseEvent) => {
      if (!isEnabled) return;

      const target = e.target as HTMLElement;
      if (!isValidKeyboardInput(target)) return;

      setActiveInput(target);
      setIsOpen(true);
    };

    const handleDocumentClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const isInput =
        target.tagName === "INPUT" || target.tagName === "TEXTAREA";
      const isKeyboard = target.closest(".keyboard-container");
      const isToggle = target.closest("[data-auth-keyboard-toggle]");

      if (isToggle) return;

      if (!isInput && !isKeyboard) {
        setIsOpen(false);
      } else if (isInput) {
        handleFocus(e);
      }
    };

    document.addEventListener("focusin", handleFocus);
    document.addEventListener("mousedown", handleDocumentClick);

    return () => {
      document.removeEventListener("focusin", handleFocus);
      document.removeEventListener("mousedown", handleDocumentClick);
    };
  }, [isEnabled]);

  return (
    <KeyboardContext.Provider
      value={{
        isOpen,
        setIsOpen,
        activeInput,
        setActiveInput,
        isEnabled,
        position,
        refreshConfig,
        showAuthToggle,
        authToggleOn,
        toggleAuthKeyboard,
      }}
    >
      {children}
    </KeyboardContext.Provider>
  );
};

export const useKeyboard = () => {
  const context = useContext(KeyboardContext);
  if (!context) {
    throw new Error("useKeyboard must be used within a KeyboardProvider");
  }
  return context;
};
